import { useTranslation } from 'react-i18next';
import { languages, formatCurrency, currencyRates } from '../i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { Globe } from 'lucide-react';

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  
  const currentLang = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    // Store preferred currency based on language
    const lang = languages.find(l => l.code === langCode);
    if (lang) {
      localStorage.setItem('preferredCurrency', lang.currency);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="gap-2 text-muted-foreground hover:text-foreground"
          data-testid="language-switcher"
        >
          <Globe className="w-4 h-4" />
          <span className="hidden sm:inline">{currentLang.flag} {currentLang.code.toUpperCase()}</span>
          <span className="sm:hidden">{currentLang.flag}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {languages.map((lang) => (
          <DropdownMenuItem 
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={`gap-3 cursor-pointer ${i18n.language === lang.code ? 'bg-primary/10' : ''}`}
            data-testid={`lang-option-${lang.code}`}
          >
            <span className="text-lg">{lang.flag}</span>
            <div className="flex-1">
              <span className="font-medium">{lang.name}</span>
              <span className="text-xs text-muted-foreground ml-2">({lang.currency})</span>
            </div>
            {i18n.language === lang.code && (
              <span className="text-primary text-sm">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Export helper hook for getting currency-formatted prices
export function useCurrency() {
  const { i18n } = useTranslation();
  const currentLang = languages.find(lang => lang.code === i18n.language) || languages[0];
  const preferredCurrency = localStorage.getItem('preferredCurrency') || currentLang.currency;
  
  const format = (amount) => {
    return formatCurrency(amount, preferredCurrency);
  };
  
  return { format, currency: preferredCurrency, rate: currencyRates[preferredCurrency] || 1 };
}
